import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { invoicePublicAPI, fmtMoney, fmtDay } from '@/lib/financeAPI';

// What the client sees from the link in their invoice email. No login.
export default function InvoicePublic() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    invoicePublicAPI.get(token, params.get('preview') === '1').then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) return <div className="min-h-screen bg-[#FBF8F2] flex items-center justify-center p-6 text-center text-[#5C6670]">This invoice link isn't valid. Please check with whoever sent it.</div>;
  if (!data) return <div className="min-h-screen bg-[#FBF8F2] flex items-center justify-center text-[#5C6670]">Loading…</div>;

  const { business: b, invoice: inv } = data;
  const t = inv.totals || {};
  const cur = inv.currency;
  const accent = b.brand_color || '#A62B1F';
  const paid = inv.status === 'paid';
  const statusText = { paid: 'Paid. Thank you!', overdue: 'Overdue', partial: 'Part paid', void: 'This invoice has been cancelled' }[inv.status];

  return (
    <div className="min-h-screen bg-[#FBF8F2] py-6 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-end mb-3">
          <a href={invoicePublicAPI.pdfUrl(token)} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md text-white" style={{ backgroundColor: accent }}>
            <Download className="w-4 h-4" />Download PDF
          </a>
        </div>
        <div className="bg-white rounded-xl border border-[#E3DDD1] overflow-hidden">
          <div className="h-1.5" style={{ backgroundColor: accent }} />
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-6">
              <div>
                {b.logo_url && <img src={b.logo_url} alt="" className="h-12 mb-3 object-contain" />}
                <div className="text-lg font-semibold text-[#1E2A33]">{b.business_name}</div>
                <div className="text-sm text-[#5C6670] whitespace-pre-line">{[b.address, b.email, b.phone, b.website, b.tax_numbers].filter(Boolean).join('\n')}</div>
              </div>
              <div className="sm:text-right">
                <div className="text-3xl font-bold tracking-tight" style={{ color: accent }}>INVOICE</div>
                <div className="text-sm text-[#5C6670] mt-1">No. <strong className="text-[#1E2A33]">{inv.number}</strong></div>
                <div className="text-sm text-[#5C6670]">Issued {fmtDay(inv.issue_date)}</div>
                <div className="text-sm text-[#5C6670]">Due {fmtDay(inv.due_date)}</div>
                {inv.po_number && <div className="text-sm text-[#5C6670]">Ref {inv.po_number}</div>}
              </div>
            </div>

            <div className={`mt-6 rounded-lg px-4 py-3 flex items-center justify-between ${paid ? 'bg-emerald-50' : 'bg-[#F3EFE7]'}`}>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#5C6670] font-semibold">Balance due</div>
                {statusText && <div className={`text-sm font-medium ${paid ? 'text-emerald-800' : inv.status === 'overdue' ? 'text-[#A62B1F]' : 'text-[#5C6670]'}`}>{statusText}</div>}
              </div>
              <div className="text-2xl font-bold text-[#1E2A33]">{fmtMoney(t.balance, cur)}</div>
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: accent }}>Bill to</div>
              <div className="text-sm text-[#1E2A33] font-semibold mt-1">{inv.client?.company || inv.client?.name}</div>
              {inv.client?.company && inv.client?.name && inv.client.name !== inv.client.company && <div className="text-sm text-[#1E2A33]">{inv.client.name}</div>}
              {inv.client?.address && <div className="text-sm text-[#5C6670] whitespace-pre-line">{inv.client.address}</div>}
              {inv.project_name && <div className="text-sm text-[#5C6670] mt-1">Project: {inv.project_name}</div>}
            </div>

            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#F3EFE7] text-[11px] uppercase tracking-wider text-[#5C6670]">
                  <th className="text-left p-2">Description</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Rate</th><th className="text-right p-2">Amount</th></tr></thead>
                <tbody>
                  {inv.items.map((it, i) => (
                    <tr key={i} className="border-b border-[#F0EBE1]">
                      <td className="p-2 whitespace-pre-line">{it.description}{it.tax && <span className="text-[11px] text-[#94A3B8] ml-1">({it.tax})</span>}</td>
                      <td className="p-2 text-right">{Number(it.quantity)}</td>
                      <td className="p-2 text-right whitespace-nowrap">{fmtMoney(it.unit_price, cur)}</td>
                      <td className="p-2 text-right whitespace-nowrap">{fmtMoney(t.lines?.[i], cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 ml-auto max-w-xs text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#5C6670]">Subtotal</span><span>{fmtMoney(t.subtotal, cur)}</span></div>
              {t.discount > 0 && <div className="flex justify-between"><span className="text-[#5C6670]">Discount</span><span>−{fmtMoney(t.discount, cur)}</span></div>}
              {(t.taxes || []).map((x) => <div key={x.id} className="flex justify-between"><span className="text-[#5C6670]">{x.name} ({x.rate}%)</span><span>{fmtMoney(x.amount, cur)}</span></div>)}
              <div className="flex justify-between font-semibold text-base border-t border-[#1E2A33] pt-1.5"><span>Total ({cur})</span><span>{fmtMoney(t.total, cur)}</span></div>
              {t.paid > 0 && <div className="flex justify-between text-emerald-700"><span>Paid</span><span>−{fmtMoney(t.paid, cur)}</span></div>}
            </div>

            {inv.hours && (
              <div className="mt-8">
                <div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: accent }}>Hours under this invoice</div>
                <div className="text-sm text-[#1E2A33] mb-2">{inv.hours.hours} h{inv.hours.retainer_hours ? ` of ${inv.hours.retainer_hours} h retainer` : ''}</div>
                <table className="w-full text-sm"><tbody>
                  {inv.hours.entries.map((e, i) => (
                    <tr key={i} className="border-b border-[#F0EBE1]"><td className="py-1.5 pr-3 whitespace-nowrap text-[#5C6670]">{fmtDay(e.date, { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                      <td className="py-1.5 pr-3">{[e.activity, e.description].filter(Boolean).join(' · ')}</td><td className="py-1.5 text-right">{e.hours} h</td></tr>
                  ))}
                </tbody></table>
              </div>
            )}

            {(inv.notes || inv.payment_instructions) && (
              <div className="mt-8 grid sm:grid-cols-2 gap-6 text-sm">
                {inv.payment_instructions && <div><div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: accent }}>How to pay</div><div className="whitespace-pre-line text-[#1E2A33]">{inv.payment_instructions}</div></div>}
                {inv.notes && <div><div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: accent }}>Notes</div><div className="whitespace-pre-line text-[#1E2A33]">{inv.notes}</div></div>}
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-[#94A3B8] mt-4">Sent with Stagehead OS</p>
      </div>
    </div>
  );
}
